import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import usersApi from "@/api/usersApi";
import UserProfileResponse from "@/model/UserProfileResponse";
import "@/pages/UsersPage/UsersPage.scss";

const PAGE_SIZE = 20;

function UsersPage() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [users, setUsers] = useState<UserProfileResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    usersApi
      .listUsers(session.accessToken, page, PAGE_SIZE, search || undefined)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setUsers(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, page, search]);

  if (!session) {
    return null;
  }

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  return (
    <div className="users-page">
      <header className="users-page__header">
        <h1>{t("users.list.title")}</h1>
        <div className="users-page__header-actions">
          <Link to="/users/new">{t("users.list.createButton")}</Link>
        </div>
      </header>

      <form className="users-page__search" onSubmit={handleSearchSubmit}>
        <input
          type="text"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t("users.list.searchPlaceholder")}
        />
        <button type="submit">{t("users.list.searchButton")}</button>
      </form>

      {isLoading && <p>{t("users.list.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("users.list.error")}</p>}
      {!isLoading && !error && users.length === 0 && (
        <p>{t("users.list.empty")}</p>
      )}

      {!isLoading && !error && users.length > 0 && (
        <>
          <table className="users-page__table">
            <thead>
              <tr>
                <th>{t("users.list.columns.name")}</th>
                <th>{t("users.list.columns.email")}</th>
                <th>{t("users.list.columns.role")}</th>
                <th>{t("users.list.columns.company")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id ?? index}>
                  <td>
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </td>
                  <td>{user.email ?? "—"}</td>
                  <td>
                    {user.role ? t(`users.create.roles.${user.role}`) : "—"}
                  </td>
                  <td>
                    {user.companyId ? (
                      <Link to={`/companies/${user.companyId}`}>
                        {t("users.list.viewCompanyLink")}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="users-page__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("users.list.pagination.previous")}
            </button>
            <span>
              {t("users.list.pagination.pageInfo", {
                page: page + 1,
                totalPages,
              })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("users.list.pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default UsersPage;
